import { useMemo, useEffect } from 'react'
import { useRouter } from 'next/router'
import NextLink from 'next/link'

import { Button } from '@wartek-id/button-v1'
import { Text } from '@wartek-id/text-v1'
import { Icon } from '@wartek-id/icon-v2'

import { Header } from 'app/KinerjaShared/components/Header'
import { useAPIKinerja } from 'api/kinerja/useAPIKinerja'
import { PENGELOLAAN_KINERJA_GURU_PATH } from 'app/KinerjaShared/constants/path'
import { Detail } from 'app/KinerjaGuruPage/components/perencanaan/Detail'
import { resolvePerencanaanDetailData } from 'app/KinerjaGuruPage/utils/perencanaan'

import type { KinerjaStatus } from 'types/kinerja'
import type { PerencanaanGuruDetailData } from 'app/KinerjaGuruPage/types/perencanaan'

const ALLOWED_STATES: Array<KinerjaStatus> = [
  'WAITING_APPROVAL',
  'REVISED',
  'READY',
]

export function CekPerencanaan() {
  const router = useRouter()

  // -----------------------------------------
  // Get perencanaan data
  // -----------------------------------------
  const {
    data: perencanaanData,
    isLoading,
    isSuccess,
  } = useAPIKinerja(
    {
      staleTime: 60 * 60 * 1000, // 1 hour
      useErrorBoundary: true,
    },
    {
      tipe: 'SPF',
    }
  )

  const resolvedPerencanaanData: PerencanaanGuruDetailData = useMemo(
    () => resolvePerencanaanDetailData(perencanaanData?.data),
    [perencanaanData?.data]
  )

  const allowedToVisit = useMemo(() => {
    return ALLOWED_STATES.includes(perencanaanData?.data?.status)
  }, [perencanaanData?.data?.status])

  // -----------------------------------------
  // Perencanaan state guard
  // -----------------------------------------
  useEffect(() => {
    if (isSuccess && !allowedToVisit) {
      router.replace(PENGELOLAAN_KINERJA_GURU_PATH)
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isLoading, allowedToVisit, isSuccess])

  return (
    <main className="max-w-7xl mx-auto py-12 px-6">
      <Header title="Perencanaan Kinerja" className="mb-9" />

      <div className="bg-white rounded-lg">
        <div className="py-9 px-6">
          <section className="space-y-4 mb-10">
            <div className="flex items-center gap-3">
              <Icon className="!text-4xl !w-9 !h-9 !leading-9">
                account_circle_outlined
              </Icon>
              <Text variant="heading-lg" as="h2">
                {perencanaanData?.data?.nama}
              </Text>
            </div>
          </section>

          <Detail
            isLoading={isLoading}
            data={resolvedPerencanaanData}
            showReview={perencanaanData?.data?.status === 'READY'}
          />
        </div>

        <div className="border-t border-subdued px-6 py-4 flex justify-end gap-5">
          <NextLink href={PENGELOLAAN_KINERJA_GURU_PATH} passHref replace>
            <Button as="a" type={undefined} color="blue" size="lg">
              Kembali ke Beranda
            </Button>
          </NextLink>
        </div>
      </div>
    </main>
  )
}
